import { lazy, Suspense } from 'react'

import { defineMessages } from 'react-intl'
import { useRouteMatch } from 'react-router'

import { Page } from '@webapp/core/components/Page'
import { useAppRoutes, useProcurifyIntl } from '@webapp/hooks'
import { userReportConfig } from '@webapp/modules/reports/config/reportConfigs'
import { useUserDetailHeaderPresenter } from '@webapp/modules/reports/features/get-user-detail-header/presenters/UseUserDetailHeaderPresenter'
import { type ReportDetailsPageParams } from '../types'

const ReportDetailViewChunk = lazy(() =>
  import('../../feature-sets/ReportDetailView').then(
    ({ ReportDetailView }) => ({
      default: ReportDetailView,
    })
  )
)

const scope = `procurify.modules.reports.pages.OrdersByUserReportDetailsPage`

const messages = defineMessages({
  pageTitle: {
    id: `${scope}.pageTitle`,
    defaultMessage: 'Orders by user',
  },
})

export const OrdersByUserReportDetailsPage = () => {
  const intl = useProcurifyIntl()
  const { url } = useAppRoutes()
  const match = useRouteMatch<ReportDetailsPageParams>()

  const entityId = Number(match.params.id)

  // Breadcrumb entity name resolved independently of the line-item fetch
  // (BHV-022) — same query key as the Feature Set's header hook, so React
  // Query dedupes the fetch.
  const {
    data: { userFullName },
  } = useUserDetailHeaderPresenter({ userId: entityId })

  return (
    <Page
      pageTitle={intl.formatMessage(messages.pageTitle)}
      breadcrumbs={[
        {
          link: url('OrdersByUserReportPage'),
          name: intl.formatMessage(messages.pageTitle),
        },
        { name: userFullName ?? '', escapeCaseFormatting: true },
      ]}
    >
      <div data-testid='orders-by-user-report-details-page'>
        <Suspense fallback={null}>
          <ReportDetailViewChunk
            reportConfig={userReportConfig}
            entityId={entityId}
          />
        </Suspense>
      </div>
    </Page>
  )
}
